import type { AnimationI, AnySceneObjectT } from "tweenly"
import { useMemo } from "react"
import { selectUndoable } from "../../../helpers/selector.helpers"
import { useAnimationActions } from "../../../hooks/useAnimationActions"
import useObject from "../../../hooks/useObject"
import { useAppSelector } from "../../../hooks/useRedux"
import { selectAnimationsMap } from "../../../store/slices/objects.slice/animation/selectAnimationsMap"
import { selectAnimationsByIds } from "../../../store/slices/objects.slice/animation/selectAnimationsByIds"
import FadingScrollContainer from "../../common/FadingScrollContainer"
import TitleHeading from "../../common/TitleHeading"
import AnimationTween from "./AnimationTools/AnimationTween"
import Easing from "./AnimationTools/Easing"
import Timing from "./AnimationTools/Timing"

interface Props {
    animationId: string
    object?: AnySceneObjectT
}

const AnimationTools = ({ animationId, object }: Props) => {
    const { findObjectById } = useObject()
    const selectedAnimationIds = useAppSelector((state) => state.activeAnimation.selected)
    const objects = useAppSelector((state) => selectUndoable(state).objects)

    const animationsMap = useAppSelector((state) =>
        selectAnimationsMap(selectUndoable(state).objects)
    )
    const animation: AnimationI | undefined = animationsMap.get(animationId)

    const selectedAnimations = useMemo(() => {
        return selectAnimationsByIds(objects, selectedAnimationIds)
    }, [selectedAnimationIds, objects])

    const { updateAnimation } = useAnimationActions()
    const setValue = (property: any, value: any) => {
        if (animation) updateAnimation(animationId, animation.objectId, property, value)
    }
    const setValues = (animations: AnimationI[], property: any, value: any) => {
        animations.forEach((animation: AnimationI) =>
            updateAnimation(animation.id, animation.objectId, property, value)
        )
    }
    const setValuesWithTransform = (
        animations: AnimationI[],
        property: string,
        transformer: (anim: AnimationI) => any
    ) => {
        animations.forEach((animation: AnimationI) =>
            updateAnimation(animation.id, animation.objectId, property, transformer(animation))
        )
    }
    const isSequenceType = animation?.tween.name === "sequence"

    const getSelectedAnimationsTitle = (): string => {
        let result: string[] = []
        selectedAnimations.forEach((animation: AnimationI) => {
            if (animation.tween.name === "sequence") {
                let object = findObjectById(animation.objectId)
                if (object)
                    if (!result.includes(object?.title)) {
                        result.push(object?.title)
                    }
            } else {
                result.push(animation.tween.name)
            }
        })
        return result.join(", ")
    }

    return (
        <FadingScrollContainer id="editor-step-4">
            {animation && (
                <div>
                    <TitleHeading
                        multiline={selectedAnimationIds.length > 1}
                        object={selectedAnimationIds.length <= 1 ? animation : undefined}
                        handleSetValue={selectedAnimationIds.length <= 1 ? setValue : undefined}
                        title={
                            selectedAnimationIds.length <= 1
                                ? undefined
                                : getSelectedAnimationsTitle()
                        }
                        disabled={isSequenceType}
                        isAnimation
                    />

                    <Timing animation={animation} selectedAnimations={selectedAnimations} />
                    {!isSequenceType && object && !["video-panel"].includes(object.type) && (
                        <Easing
                            animation={animation}
                            selectedAnimations={selectedAnimations}
                            handleSetValue={setValue}
                            handleSetValues={setValues}
                            handleSetValuesWithTransform={setValuesWithTransform}
                        />
                    )}
                    {!isSequenceType && selectedAnimationIds.length < 2 && (
                        <AnimationTween
                            animation={animation}
                            handleSetValue={setValue}
                            object={object}
                        />
                    )}
                </div>
            )}
        </FadingScrollContainer>
    )
}

export default AnimationTools
